/** Parse a positive, finite number from an env string, falling back to a default. */
function envNumber(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * WebSocket endpoint: an explicit non-empty VITE_WS_URL wins (dev passes the
 * resolved server port through it); otherwise derive from the page origin —
 * the serving layer (server static mount, dev-fast shim) proxies the upgrade.
 */
function resolveWsUrl(): string {
  const override = import.meta.env.VITE_WS_URL;
  if (override) return override;
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/ws`;
}

/** Client configuration resolved from Vite environment variables */
export const CLIENT_CONFIG = {
  apiBase: import.meta.env.VITE_API_BASE ?? '',
  wsUrl: resolveWsUrl(),
  googleMapsKey: import.meta.env.VITE_GOOGLE_MAPS_KEY ?? '',
  googleMapsMapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID ?? '',
  vertexUrl: import.meta.env.VITE_VERTEX_URL ?? '',
  /** Multiplier applied to true satellite altitude for the 3D orbital shell. */
  satelliteShellExaggeration: envNumber(import.meta.env.VITE_SAT_SHELL_EXAGGERATION, 3),
  /** Whether to draw the 3D coverage cone (satellite shell → footprint ring) on select. */
  coverageConeEnabled: import.meta.env.VITE_COVERAGE_CONE !== 'false',
  /** Default render cap for a viewport-culled layer when its manifest omits maxRendered. */
  viewportDefaultMaxRendered: envNumber(import.meta.env.VITE_VIEWPORT_MAX_RENDERED, 400),
  /** Margin (fraction of viewport span) added around the visible box before culling. */
  viewportMarginFraction: envNumber(import.meta.env.VITE_VIEWPORT_MARGIN, 0.25),
  /** Min 2D zoom at which a culled layer renders; below this it is skipped entirely. */
  viewportMinZoom2D: envNumber(import.meta.env.VITE_VIEWPORT_MIN_ZOOM_2D, 6),
  /** Max 3D camera range (m) at which a culled layer renders; above this it is skipped. */
  viewportMaxRange3D: envNumber(import.meta.env.VITE_VIEWPORT_MAX_RANGE_3D, 2_500_000),
} as const;

/** Default map center: CONUS (lat/lng for Google Maps) */
export const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 39.0, lng: -98.0 };

/** Default map zoom level */
export const DEFAULT_ZOOM = 4;

/** Default 3D camera range (meters) for CONUS view */
export const DEFAULT_3D_RANGE = 10_000_000;
